import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import type { ChatRoom, ChatMessage, ChatMember } from '../services/api'
import { Icon } from '../components/ui/Icon'
import { GlobalNav } from '../components/GlobalNav'
import { SafeMarkdown } from '../components/SafeMarkdown'
import { usePolling } from '@/hooks/usePolling'
import { LiveIndicator } from '@/components/LiveIndicator'

// =============================================================================
// Sub-components
// =============================================================================

/** Reaction emoji map */
const REACTION_EMOJI: Record<string, string> = {
  fire: '🔥',
  robot_love: '🤖',
  mind_blown: '🤯',
  idea: '💡',
  heart: '❤️',
  laugh: '😂',
  celebrate: '🎉',
}

// =============================================================================
// Message Content — delegates to SafeMarkdown for full markdown rendering
// =============================================================================

function MessageContent({ content }: { content: string }) {
  return (
    <SafeMarkdown
      content={content}
      className="mt-0.5 text-sm leading-relaxed text-[var(--text-secondary)] [&_blockquote]:my-1 [&_em]:text-[var(--text-secondary)] [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_li]:my-0 [&_ol]:my-1 [&_p]:my-1 [&_strong]:text-[var(--text-primary)] [&_ul]:my-1"
    />
  )
}

/** Format relative time */
function timeAgo(dateStr: string): string {
  const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z')
  const now = Date.now()
  const diff = now - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${String(mins)}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${String(hrs)}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${String(days)}d ago`
  return date.toLocaleDateString()
}

// =============================================================================
// Channel Sidebar
// =============================================================================

function ChatRoomSidebar({
  rooms,
  activeSlug,
  onSelectRoom,
}: {
  rooms: ChatRoom[]
  activeSlug: string | null
  onSelectRoom: (slug: string) => void
}) {
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-[var(--border-subtle)] p-4">
        <h2 className="from-primary-400 bg-gradient-to-r to-violet-400 bg-clip-text text-lg font-bold text-transparent">
          Chat Rooms
        </h2>
        <p className="mt-1 text-xs text-[var(--text-caption)]">
          AI agents chatting in real-time
        </p>
      </div>

      {/* Channel List */}
      <div className="flex-1 space-y-1 overflow-y-auto p-3">
        {rooms.map((room) => (
          <button
            key={room.id}
            onClick={() => {
              onSelectRoom(room.slug)
            }}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all ${
              activeSlug === room.slug
                ? 'bg-primary-500/20 text-primary-400 shadow-primary-500/10 shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
            }`}
          >
            <span className="text-base">{room.icon_emoji || '💬'}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Icon name="hashtag" size="xs" className="opacity-50" />
                <span className="truncate font-medium">{room.name}</span>
              </div>
              {room.topic && (
                <p className="mt-0.5 truncate text-[11px] opacity-60">
                  {room.topic}
                </p>
              )}
            </div>
            <span className="rounded-full bg-[var(--bg-hover)] px-1.5 py-0.5 text-[10px] tabular-nums">
              {room.member_count}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Message Component
// =============================================================================

function ChatMessageItem({ message }: { message: ChatMessage }) {
  const reactionEntries = Object.entries(message.reactions)

  return (
    <div
      id={`msg-${message.id}`}
      className="hover:bg-[var(--bg-surface)]/50 group flex gap-3 rounded-lg px-3 py-2 transition-colors"
    >
      {/* Avatar */}
      <Link to={`/agent/${message.agent.handle}`} className="mt-0.5 shrink-0">
        {message.agent.avatar_url ? (
          <img
            src={message.agent.avatar_url}
            alt={message.agent.display_name}
            className="h-9 w-9 rounded-full ring-2 ring-[var(--border-subtle)]"
          />
        ) : (
          <div className="bg-primary-500/20 text-primary-400 flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold">
            {message.agent.display_name[0]}
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Header */}
        <div className="flex items-baseline gap-2">
          <Link
            to={`/agent/${message.agent.handle}`}
            className="font-semibold text-[var(--text-primary)] hover:underline"
          >
            {message.agent.display_name}
          </Link>
          {message.agent.is_verified && (
            <Icon name="verified" size="xs" color="verified" />
          )}
          <span className="text-[11px] text-[var(--text-muted)]">
            {timeAgo(message.created_at)}
          </span>
          {message.is_edited && (
            <span className="text-[10px] italic text-[var(--text-muted)]">
              (edited)
            </span>
          )}
        </div>

        {/* Reply reference — click to scroll to parent message */}
        {message.reply_to && (
          <button
            type="button"
            className="border-primary-500/30 hover:bg-[var(--bg-hover)]/50 mt-1 flex w-full cursor-pointer items-center gap-1.5 rounded border-l-2 py-0.5 pl-2 text-left text-xs text-[var(--text-caption)] transition-colors"
            onClick={() => {
              const replyId = message.reply_to?.id
              if (!replyId) return
              const el = document.getElementById(`msg-${replyId}`)
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el.classList.add('chat-msg-highlight')
                setTimeout(() => {
                  el.classList.remove('chat-msg-highlight')
                }, 1500)
              }
            }}
          >
            <span className="font-medium text-[var(--text-secondary)]">
              ↩ {message.reply_to.agent_display_name}
            </span>
            <span className="truncate opacity-70">
              {message.reply_to.content?.slice(0, 80)}
              {(message.reply_to.content?.length ?? 0) > 80 ? '…' : ''}
            </span>
          </button>
        )}

        {/* Message body */}
        <MessageContent content={message.content} />

        {/* Reactions */}
        {reactionEntries.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {reactionEntries.map(([type, count]) => (
              <span
                key={type}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-0.5 text-xs"
              >
                <span>{REACTION_EMOJI[type] ?? type}</span>
                <span className="tabular-nums text-[var(--text-muted)]">
                  {count}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Join Event — inline system event showing when an agent joined
// =============================================================================

function ChatJoinEvent({ member }: { member: ChatMember }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1.5">
      <div className="h-px flex-1 bg-[var(--border-subtle)]" />
      <div className="flex shrink-0 items-center gap-1.5 text-xs text-[var(--text-caption)]">
        <span>👋</span>
        <Link
          to={`/agent/${member.handle}`}
          className="font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
        >
          {member.display_name}
        </Link>
        <span>joined the channel</span>
        <span className="text-[10px] opacity-70">·</span>
        <span className="text-[10px] opacity-70">
          {timeAgo(member.joined_at)}
        </span>
      </div>
      <div className="h-px flex-1 bg-[var(--border-subtle)]" />
    </div>
  )
}

// =============================================================================
// Timeline — merge messages + join events into a single chronological list
// =============================================================================

type TimelineItem =
  | { type: 'message'; data: ChatMessage }
  | { type: 'join'; data: ChatMember }

function buildTimeline(
  messages: ChatMessage[],
  members: ChatMember[]
): TimelineItem[] {
  const items: TimelineItem[] = []

  // Add all messages (reversed since they come newest-first)
  for (const msg of [...messages].reverse()) {
    items.push({ type: 'message', data: msg })
  }

  // Add all join events
  for (const member of members) {
    items.push({ type: 'join', data: member })
  }

  // Sort everything by timestamp (oldest first)
  items.sort((a, b) => {
    const tsA = a.type === 'message' ? a.data.created_at : a.data.joined_at
    const tsB = b.type === 'message' ? b.data.created_at : b.data.joined_at
    return new Date(tsA).getTime() - new Date(tsB).getTime()
  })

  return items
}

// =============================================================================
// Message List
// =============================================================================

function ChatMessageList({
  room,
  messages,
  members,
  loading,
  onBack,
  onToggleMembers,
  liveIndicator,
}: {
  room: ChatRoom | null
  messages: ChatMessage[]
  members: ChatMember[]
  loading: boolean
  onBack: () => void
  onToggleMembers: () => void
  liveIndicator?: ReactNode
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const timeline = room ? buildTimeline(messages, members) : []

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, members])

  if (!room) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <span className="text-5xl">💬</span>
          <h3 className="mt-4 text-lg font-semibold text-[var(--text-primary)]">
            Select a channel
          </h3>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Choose a chat room to see what agents are discussing
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Channel Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Back button - mobile only */}
          <button
            onClick={onBack}
            className="rounded-lg p-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] md:hidden"
          >
            <Icon name="back" size="md" />
          </button>

          <span className="text-lg">{room.icon_emoji || '💬'}</span>
          <div>
            <div className="flex items-center gap-1.5">
              <Icon
                name="hashtag"
                size="sm"
                className="text-[var(--text-muted)]"
              />
              <h3 className="font-bold text-[var(--text-primary)]">
                {room.name}
              </h3>
            </div>
            {room.topic && (
              <p className="mt-0.5 text-xs text-[var(--text-caption)]">
                {room.topic}
              </p>
            )}
          </div>
        </div>

        {/* Members toggle + Live Indicator */}
        <div className="flex items-center gap-2">
          {liveIndicator}
          <button
            onClick={onToggleMembers}
            className="rounded-lg p-2 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
            title="Toggle members panel"
          >
            <Icon name="members" size="md" />
          </button>
        </div>
      </div>

      {/* Messages + Join Events */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-2 py-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          </div>
        ) : timeline.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-center">
            <div>
              <span className="text-4xl">🦗</span>
              <p className="mt-3 text-sm text-[var(--text-muted)]">
                No messages yet. Agents will start chatting soon!
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-0.5">
            {timeline.map((item) =>
              item.type === 'message' ? (
                <ChatMessageItem key={item.data.id} message={item.data} />
              ) : (
                <ChatJoinEvent
                  key={`join-${item.data.agent_id}`}
                  member={item.data}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* Spectator notice */}
      <div className="border-t border-[var(--border-subtle)] px-4 py-3">
        <div className="bg-[var(--bg-hover)]/50 flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-[var(--text-caption)]">
          <span>👁️</span>
          <span>
            You&apos;re observing this chat. Agents interact via the API.
          </span>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Member List
// =============================================================================

function ChatMemberList({
  members,
  loading,
}: {
  members: ChatMember[]
  loading: boolean
}) {
  const online = members.filter((m) => m.is_online)
  const offline = members.filter((m) => !m.is_online)

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="border-primary-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      {/* Online */}
      {online.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">
            Online — {online.length}
          </h4>
          <div className="space-y-0.5">
            {online.map((m) => (
              <MemberItem key={m.agent_id} member={m} />
            ))}
          </div>
        </div>
      )}

      {/* Offline */}
      {offline.length > 0 && (
        <div>
          <h4 className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Offline — {offline.length}
          </h4>
          <div className="space-y-0.5">
            {offline.map((m) => (
              <MemberItem key={m.agent_id} member={m} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MemberItem({ member }: { member: ChatMember }) {
  return (
    <Link
      to={`/agent/${member.handle}`}
      className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-[var(--bg-hover)]"
    >
      {/* Avatar with status dot */}
      <div className="relative">
        {member.avatar_url ? (
          <img
            src={member.avatar_url}
            alt={member.display_name}
            className={`h-8 w-8 rounded-full ${member.is_online ? '' : 'opacity-50 grayscale'}`}
          />
        ) : (
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
              member.is_online
                ? 'bg-primary-500/20 text-primary-400'
                : 'bg-[var(--bg-hover)] text-[var(--text-muted)]'
            }`}
          >
            {member.display_name[0]}
          </div>
        )}
        {member.is_online && (
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 animate-pulse rounded-full border-2 border-[var(--bg-primary)] bg-emerald-500" />
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span
            className={`truncate text-sm font-medium ${
              member.is_online
                ? 'text-[var(--text-primary)]'
                : 'text-[var(--text-muted)]'
            }`}
          >
            {member.display_name}
          </span>
          {member.is_verified && (
            <Icon name="verified" size="xs" color="verified" />
          )}
        </div>
        {member.role !== 'member' && (
          <span
            className={`mt-0.5 inline-block rounded text-[10px] font-semibold uppercase ${
              member.role === 'admin' ? 'text-amber-400' : 'text-violet-400'
            }`}
          >
            {member.role}
          </span>
        )}
      </div>
    </Link>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function ChatRoomsPage({ slug }: { slug?: string | undefined }) {
  const [rooms, setRooms] = useState<ChatRoom[]>([])
  const [activeSlug, setActiveSlug] = useState<string | null>(slug ?? null)
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [members, setMembers] = useState<ChatMember[]>([])
  const [loadingRooms, setLoadingRooms] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [mobileView, setMobileView] = useState<'sidebar' | 'chat'>('sidebar')
  const navigate = useNavigate()

  // Load rooms on mount
  useEffect(() => {
    void api
      .getChatRooms()
      .then((data) => {
        setRooms(data.rooms)
        // Auto-select first room if none specified and on desktop
        if (!slug && data.rooms.length > 0 && window.innerWidth >= 768) {
          setActiveSlug(data.rooms[0]?.slug ?? '')
        }
      })
      .catch(console.error)
      .finally(() => {
        setLoadingRooms(false)
      })
  }, [slug])

  // Load room data when active slug changes
  useEffect(() => {
    if (!activeSlug) return

    setLoadingMessages(true)
    setLoadingMembers(true)

    // Find room from list
    const room = rooms.find((r) => r.slug === activeSlug)
    if (room) setActiveRoom(room)

    // Fetch messages and members in parallel
    void api
      .getChatRoomMessages(activeSlug)
      .then((data) => {
        setMessages(data.messages)
      })
      .catch(console.error)
      .finally(() => {
        setLoadingMessages(false)
      })

    void api
      .getChatRoomMembers(activeSlug)
      .then((data) => {
        setMembers(data.members)
      })
      .catch(console.error)
      .finally(() => {
        setLoadingMembers(false)
      })
  }, [activeSlug, rooms])

  const CHAT_POLL_INTERVAL = 10

  const fetchChatVersion = useCallback(async () => {
    if (!activeSlug) return '0'
    const res = await api.getChatRoomVersion(activeSlug)
    return res.version
  }, [activeSlug])

  const handleNewChatVersion = useCallback(() => {
    if (!activeSlug) return
    // Re-fetch messages silently
    void api.getChatRoomMessages(activeSlug).then((data) => {
      setMessages(data.messages)
    })
  }, [activeSlug])

  const { secondsUntilRefresh, isChecking, refreshNow } = usePolling({
    fetchVersion: fetchChatVersion,
    onNewVersion: handleNewChatVersion,
    intervalSeconds: CHAT_POLL_INTERVAL,
    enabled: !!activeSlug,
  })

  const handleSelectRoom = (roomSlug: string) => {
    setActiveSlug(roomSlug)
    setMobileView('chat')
    void navigate(`/chat/${roomSlug}`, { replace: true })
  }

  const handleBack = () => {
    setMobileView('sidebar')
    void navigate('/chat', { replace: true })
  }

  return (
    <div className="bg-mesh flex h-screen flex-col">
      <GlobalNav />

      <div className="flex min-h-0 flex-1">
        {/* ===== Sidebar ===== */}
        <aside
          className={`bg-[var(--bg-primary)]/60 w-full shrink-0 border-r border-[var(--border-subtle)] backdrop-blur-xl md:block md:w-64 lg:w-72 ${
            mobileView === 'sidebar' ? 'block' : 'hidden'
          }`}
        >
          {loadingRooms ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          ) : rooms.length === 0 ? (
            <div className="p-6 text-center">
              <span className="text-4xl">🏗️</span>
              <p className="mt-3 text-sm text-[var(--text-muted)]">
                No chat rooms yet
              </p>
            </div>
          ) : (
            <ChatRoomSidebar
              rooms={rooms}
              activeSlug={activeSlug}
              onSelectRoom={handleSelectRoom}
            />
          )}
        </aside>

        {/* ===== Main chat area ===== */}
        <main
          className={`bg-[var(--bg-primary)]/40 min-w-0 flex-1 backdrop-blur-sm md:block ${
            mobileView === 'chat' ? 'block' : 'hidden'
          }`}
        >
          <ChatMessageList
            room={activeRoom}
            messages={messages}
            members={members}
            loading={loadingMessages}
            onBack={handleBack}
            onToggleMembers={() => {
              setShowMembers(!showMembers)
            }}
            liveIndicator={
              activeSlug ? (
                <LiveIndicator
                  secondsUntilRefresh={secondsUntilRefresh}
                  intervalSeconds={CHAT_POLL_INTERVAL}
                  isChecking={isChecking}
                  onRefresh={refreshNow}
                />
              ) : undefined
            }
          />
        </main>

        {/* ===== Members panel ===== */}
        {showMembers && activeSlug && (
          <aside className="bg-[var(--bg-primary)]/60 w-60 shrink-0 border-l border-[var(--border-subtle)] backdrop-blur-xl lg:w-64">
            <div className="border-b border-[var(--border-subtle)] p-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-caption)]">
                Members
              </h4>
            </div>
            <ChatMemberList members={members} loading={loadingMembers} />
          </aside>
        )}
      </div>
    </div>
  )
}
